import { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';

interface Inquiry {
  id: string;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  product_interest: string | null;
  industry: string | null;
  message: string | null;
  status: string;
  created_at: string;
}

const statusOptions = ['new', 'read', 'replied', 'archived'];

export default function AdminInquiriesPage() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase.from('inquiries').select('*').order('created_at', { ascending: false });

      if (search) query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,product_interest.ilike.%${search}%`);
      if (filterStatus) query = query.eq('status', filterStatus);

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      setInquiries((data || []) as Inquiry[]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error: updateErr } = await supabase.from('inquiries').update({ status }).eq('id', id);
      if (updateErr) throw updateErr;
      fetchInquiries();
      if (selectedInquiry?.id === id) {
        setSelectedInquiry((prev) => prev ? { ...prev, status } : null);
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this inquiry permanently?')) return;
    try {
      const { error: delErr } = await supabase.from('inquiries').delete().eq('id', id);
      if (delErr) throw delErr;
      if (selectedInquiry?.id === id) setSelectedInquiry(null);
      fetchInquiries();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-green-100 text-green-700',
      read: 'bg-secondary-100 text-secondary-700',
      replied: 'bg-accent-100 text-accent-700',
      archived: 'bg-foreground-100 text-foreground-500',
    };
    return `text-xs px-2 py-0.5 rounded-full font-medium ${colors[status] || 'bg-foreground-100 text-foreground-500'}`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900">Inquiries</h1>
          <p className="text-sm text-foreground-500 mt-1">Manage quote requests and client messages</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              placeholder="Search by name, email, or product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 cursor-pointer"
          >
            <option value="">All Status</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
            ) : inquiries.length === 0 ? (
              <div className="bg-background-50 border border-background-200 rounded-xl p-12 text-center">
                <div className="w-12 h-12 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-open-line text-xl text-foreground-400" />
                </div>
                <p className="text-foreground-900 font-medium">No inquiries found</p>
                <p className="text-sm text-foreground-500 mt-1">Inquiries from your contact form will appear here.</p>
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-100">
                        <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs">From</th>
                        <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden md:table-cell">Interest</th>
                        <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs">Status</th>
                        <th className="text-right py-3 px-4 font-medium text-foreground-600 text-xs hidden sm:table-cell">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiries.map((inq) => (
                        <tr
                          key={inq.id}
                          onClick={() => setSelectedInquiry(inq)}
                          className={`border-b border-background-100 hover:bg-background-50/50 transition-colors cursor-pointer ${
                            selectedInquiry?.id === inq.id ? 'bg-primary-50/50' : ''
                          }`}
                        >
                          <td className="py-3 px-4">
                            <p className="font-medium text-foreground-900 truncate max-w-[180px]">{inq.name}</p>
                            <p className="text-xs text-foreground-500 truncate max-w-[180px]">{inq.email}</p>
                          </td>
                          <td className="py-3 px-4 text-foreground-600 hidden md:table-cell truncate max-w-[160px]">
                            {inq.product_interest || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <span className={statusBadge(inq.status)}>{inq.status}</span>
                          </td>
                          <td className="py-3 px-4 text-right text-xs text-foreground-500 hidden sm:table-cell whitespace-nowrap">
                            {new Date(inq.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 border-t border-background-200 text-xs text-foreground-500">
                  {inquiries.length} inquiry{inquiries.length !== 1 ? 'ies' : ''}
                </div>
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedInquiry ? (
              <div className="bg-background-50 border border-background-200 rounded-xl p-5 sticky top-24">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900">{selectedInquiry.name}</h3>
                    <p className="text-xs text-foreground-500">{selectedInquiry.email}</p>
                  </div>
                  <span className={statusBadge(selectedInquiry.status)}>{selectedInquiry.status}</span>
                </div>

                <div className="space-y-3 text-sm mb-5">
                  {selectedInquiry.company && (
                    <div>
                      <p className="text-xs text-foreground-500">Company</p>
                      <p className="text-foreground-800">{selectedInquiry.company}</p>
                    </div>
                  )}
                  {selectedInquiry.phone && (
                    <div>
                      <p className="text-xs text-foreground-500">Phone</p>
                      <p className="text-foreground-800">{selectedInquiry.phone}</p>
                    </div>
                  )}
                  {selectedInquiry.product_interest && (
                    <div>
                      <p className="text-xs text-foreground-500">Product Interest</p>
                      <p className="text-foreground-800">{selectedInquiry.product_interest}</p>
                    </div>
                  )}
                  {selectedInquiry.industry && (
                    <div>
                      <p className="text-xs text-foreground-500">Industry</p>
                      <p className="text-foreground-800">{selectedInquiry.industry}</p>
                    </div>
                  )}
                  {selectedInquiry.message && (
                    <div>
                      <p className="text-xs text-foreground-500">Message</p>
                      <p className="text-foreground-800 whitespace-pre-wrap">{selectedInquiry.message}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-foreground-500">Received</p>
                    <p className="text-foreground-800">{new Date(selectedInquiry.created_at).toLocaleString()}</p>
                  </div>
                </div>

                <div className="border-t border-background-200 pt-4 space-y-3">
                  <p className="text-xs font-medium text-foreground-600">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(selectedInquiry.id, s)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                          selectedInquiry.status === s
                            ? 'bg-primary-500 text-background-50 border-primary-500'
                            : 'bg-background-50 text-foreground-600 border-background-300 hover:border-foreground-300'
                        }`}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => handleDelete(selectedInquiry.id)}
                  className="mt-4 w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-1" />
                  Delete Inquiry
                </button>
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200 rounded-xl p-8 text-center">
                <div className="w-12 h-12 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-arrow-left-line text-xl text-foreground-400" />
                </div>
                <p className="text-sm text-foreground-500">Select an inquiry to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}